import React, { useState, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Search, FileText, Download, Printer, Copy, X, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, RefreshCw, Plus, Filter, Calendar,
  MoreHorizontal, Edit, Save, Trash2, List, LayoutGrid, ChevronDown, ChevronUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MobileFilterToggle, MobileFilterClose, sidebarClasses } from "@/components/PageWithSidebar";

const STATUS_OPTIONS = [
  { key: "processing", label: "Đang xử lý" },
  { key: "completed", label: "Hoàn thành" },
  { key: "undeliverable", label: "Không giao được" },
  { key: "cancelled", label: "Đã hủy" },
];

const DELIVERY_STATUS_OPTIONS = [
  { key: "cho_xu_ly", label: "Chờ xử lý" },
  { key: "dang_lay_hang", label: "Đang lấy hàng" },
  { key: "dang_giao_hang", label: "Đang giao hàng" },
  { key: "giao_thanh_cong", label: "Giao thành công" },
  { key: "dang_chuyen_hoan", label: "Đang chuyển hoàn" },
  { key: "da_chuyen_hoan", label: "Đã chuyển hoàn" },
  { key: "da_huy", label: "Đã hủy" },
];

const PAYMENT_METHOD_OPTIONS = [
  { key: "tien_mat", label: "Tiền mặt" },
  { key: "the", label: "Thẻ" },
  { key: "chuyen_khoan", label: "Chuyển khoản" },
  { key: "diem", label: "Điểm" },
  { key: "vi_dien_tu", label: "Ví điện tử" },
];

const EINVOICE_STATUS_OPTIONS = [
  { key: "all", label: "Tất cả" },
  { key: "da_phat_hanh", label: "Đã phát hành" },
  { key: "dang_phat_hanh", label: "Đang phát hành" },
  { key: "phat_hanh_loi", label: "Phát hành lỗi" },
  { key: "chua_phat_hanh", label: "Chưa phát hành" },
];

const TIME_OPTIONS = [
  { key: "today", label: "Hôm nay" },
  { key: "yesterday", label: "Hôm qua" },
  { key: "this_week", label: "Tuần này" },
  { key: "this_month", label: "Tháng này" },
  { key: "custom", label: "Lựa chọn khác" },
];

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      // Try parsing as "YYYY-MM-DD HH:mm:ss" or similar
      if (dateStr.includes("T")) {
        const parts = dateStr.split("T");
        const datePart = parts[0].split("-");
        const timePart = (parts[1] || "").split(":").slice(0, 2).join(":");
        return `${datePart[2]}/${datePart[1]}/${datePart[0]} ${timePart}`;
      }
      return dateStr;
    }
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const mins = String(d.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch {
    return dateStr;
  }
}

export default function InvoicesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Filter states
  const [searchCode, setSearchCode] = useState("");
  const [searchProduct, setSearchProduct] = useState("");
  const [expandedSearch, setExpandedSearch] = useState(false);
  const [timeFilter, setTimeFilter] = useState("today");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilters, setStatusFilters] = useState(["processing", "completed"]);
  const [deliveryFilters, setDeliveryFilters] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState("");
  const [roomFilter, setRoomFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [einvoiceFilter, setEinvoiceFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // UI states
  const [showFilter, setShowFilter] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [detailTab, setDetailTab] = useState("info"); // info | payments
  const [selectedIds, setSelectedIds] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [viewMode, setViewMode] = useState("table");

  // Build query params
  const queryParams = useMemo(() => ({
    search: searchCode || undefined,
    search_product: searchProduct || undefined,
    time_filter: timeFilter,
    date_from: timeFilter === "custom" ? dateFrom : undefined,
    date_to: timeFilter === "custom" ? dateTo : undefined,
    status: statusFilters.length > 0 ? statusFilters.join(",") : undefined,
    delivery_status: deliveryFilters.length > 0 ? deliveryFilters.join(",") : undefined,
    payment_method: paymentFilter || undefined,
    room_table: roomFilter || undefined,
    sales_channel: channelFilter || undefined,
    einvoice_status: einvoiceFilter !== "all" ? einvoiceFilter : undefined,
    page,
    page_size: pageSize,
  }), [searchCode, searchProduct, timeFilter, dateFrom, dateTo, statusFilters, deliveryFilters, paymentFilter, roomFilter, channelFilter, einvoiceFilter, page, pageSize]);

  // Fetch invoices
  const { data: invoicesData, isLoading, refetch } = useQuery({
    queryKey: ["invoices", queryParams],
    queryFn: () => api.get("/invoices", { params: queryParams }).then(r => r.data),
    keepPreviousData: true,
  });

  const invoices = invoicesData?.invoices || [];
  const totalCount = invoicesData?.total || 0;
  const summary = invoicesData?.summary || {};
  const totalPages = Math.ceil(totalCount / pageSize);

  // Toggle status filter
  const toggleStatus = (key) => {
    setStatusFilters(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
    setPage(1);
  };

  const toggleDelivery = (key) => {
    setDeliveryFilters(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
    setPage(1);
  };

  // Sync from KiotViet
  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/invoices/sync", null, { params: { days: 7 } });
      toast.success(res.data.message || "Đồng bộ thành công");
      refetch();
    } catch (err) {
      toast.error("Lỗi đồng bộ: " + (err.response?.data?.detail || err.message));
    } finally {
      setSyncing(false);
    }
  };

  // Cancel invoice
  const handleCancel = async (id) => {
    try {
      await api.post(`/invoices/${id}/cancel`);
      toast.success("Đã hủy hóa đơn");
      refetch();
      setCancelDialog(null);
      setExpandedRow(null);
    } catch (err) {
      toast.error("Lỗi: " + (err.response?.data?.detail || err.message));
    }
  };

  // Copy invoice
  const handleCopy = async (id) => {
    try {
      const res = await api.post(`/invoices/${id}/copy`);
      toast.success(res.data.message || "Đã sao chép");
    } catch (err) {
      toast.error("Lỗi: " + (err.response?.data?.detail || err.message));
    }
  };

  // Print invoice
  const handlePrint = (invoice) => {
    const printContent = `
      <html><head><title>Hóa đơn ${invoice.code}</title>
      <style>body{font-family:monospace;padding:20px;max-width:300px;margin:0 auto}
      table{width:100%;border-collapse:collapse}td,th{padding:4px;text-align:left;border-bottom:1px dashed #ccc}
      .right{text-align:right}.center{text-align:center}.bold{font-weight:bold}
      h2{text-align:center;margin-bottom:5px}p{margin:3px 0}</style></head>
      <body><h2>KARINOX Arena</h2><p class="center">Hóa đơn bán hàng</p><hr/>
      <p><b>Mã HĐ:</b> ${invoice.code}</p>
      <p><b>Ngày:</b> ${formatDateTime(invoice.purchase_date)}</p>
      <p><b>Khách hàng:</b> ${invoice.customer_name}</p>
      <p><b>Thu ngân:</b> ${invoice.staff_name || invoice.creator_name || ""}</p><hr/>
      <table><tr><th>Món</th><th class="right">SL</th><th class="right">Thành tiền</th></tr>
      ${(invoice.details || []).map(d => `<tr><td>${d.product_name}</td><td class="right">${d.quantity}</td><td class="right">${formatVND(d.total)}</td></tr>`).join("")}
      </table><hr/>
      <p class="bold">Tổng: ${formatVND(invoice.total)}</p>
      <p>Giảm giá: ${formatVND(invoice.discount)}</p>
      <p class="bold">Khách trả: ${formatVND(invoice.customer_paid)}</p>
      <hr/><p class="center">Cảm ơn quý khách!</p></body></html>`;
    const win = window.open("", "_blank");
    win.document.write(printContent);
    win.document.close();
    win.print();
  };

  // Export
  const handleExport = () => {
    // Simple CSV export
    const headers = ["Mã hóa đơn", "Thời gian", "Khách hàng", "Tổng tiền", "Giảm giá", "Thuế", "Khách đã trả", "Trạng thái"];
    const rows = invoices.map(inv => [
      inv.code, formatDateTime(inv.purchase_date), inv.customer_name,
      inv.total, inv.discount, inv.tax, inv.customer_paid, inv.status_label,
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hoadon_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Đã xuất file CSV");
  };

  // Row expand toggle
  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
    setDetailTab("info");
  };

  // Select all toggle
  const toggleSelectAll = () => {
    if (selectedIds.length === invoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map(i => i.id));
    }
  };

  return (
    <div className="flex h-full bg-white dark:bg-slate-900" data-testid="invoices-page">
      {/* Left Sidebar Filters */}
      <aside className={sidebarClasses(showFilter)} data-testid="invoices-filter-panel">
        <MobileFilterClose setShowFilter={setShowFilter} />
        {/* Search */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Tìm kiếm
            <ChevronUp className="w-4 h-4" />
          </h4>
          <Input
            placeholder="Theo mã hóa đơn"
            value={searchCode}
            onChange={e => { setSearchCode(e.target.value); setPage(1); }}
            className="mb-2 text-sm"
            data-testid="search-invoice-code"
          />
          <Input
            placeholder="Theo mã, tên hàng"
            value={searchProduct}
            onChange={e => { setSearchProduct(e.target.value); setPage(1); }}
            className="mb-2 text-sm"
            data-testid="search-product-name"
          />
          <button
            onClick={() => setExpandedSearch(!expandedSearch)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
          >
            {expandedSearch ? "Thu gọn" : "Mở rộng"} {expandedSearch ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Time Filter */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Thời gian
            <ChevronUp className="w-4 h-4" />
          </h4>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="time"
                checked={timeFilter !== "custom"}
                onChange={() => setTimeFilter("today")}
                className="accent-blue-600"
              />
              <select
                value={timeFilter === "custom" ? "today" : timeFilter}
                onChange={e => { setTimeFilter(e.target.value); setPage(1); }}
                className="text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-700 dark:text-slate-200 flex-1"
                data-testid="time-filter-select"
              >
                {TIME_OPTIONS.filter(t => t.key !== "custom").map(t => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="time"
                checked={timeFilter === "custom"}
                onChange={() => setTimeFilter("custom")}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-600 dark:text-slate-400">Lựa chọn khác</span>
              <Calendar className="w-4 h-4 text-gray-400" />
            </label>
            {timeFilter === "custom" && (
              <div className="pl-6 space-y-1">
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="text-xs" />
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="text-xs" />
              </div>
            )}
          </div>
        </div>

        {/* Status Filter */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Trạng thái
            <ChevronUp className="w-4 h-4" />
          </h4>
          <div className="space-y-1.5">
            {STATUS_OPTIONS.map(opt => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={statusFilters.includes(opt.key)}
                  onCheckedChange={() => toggleStatus(opt.key)}
                  data-testid={`status-filter-${opt.key}`}
                />
                <span className="text-sm text-gray-600 dark:text-slate-400">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Delivery Status Filter */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Trạng thái giao hàng
            <ChevronUp className="w-4 h-4" />
          </h4>
          <div className="space-y-1.5">
            {DELIVERY_STATUS_OPTIONS.map(opt => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={deliveryFilters.includes(opt.key)}
                  onCheckedChange={() => toggleDelivery(opt.key)}
                  data-testid={`delivery-filter-${opt.key}`}
                />
                <span className="text-sm text-gray-600 dark:text-slate-400">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Payment Method */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Phương thức thanh toán
            <ChevronUp className="w-4 h-4" />
          </h4>
          <div className="space-y-1.5">
            {PAYMENT_METHOD_OPTIONS.map(opt => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={paymentFilter === opt.key}
                  onCheckedChange={() => { setPaymentFilter(paymentFilter === opt.key ? "" : opt.key); setPage(1); }}
                  data-testid={`payment-filter-${opt.key}`}
                />
                <span className="text-sm text-gray-600 dark:text-slate-400">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Price List */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Bảng giá
            <ChevronUp className="w-4 h-4" />
          </h4>
          <Input placeholder="Chọn bảng giá..." className="text-sm" disabled />
        </div>

        {/* Room/Table */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Phòng/Bàn
            <ChevronUp className="w-4 h-4" />
          </h4>
          <Input
            placeholder="Chọn khu vực"
            className="text-sm mb-2"
            value={roomFilter}
            onChange={e => { setRoomFilter(e.target.value); setPage(1); }}
            data-testid="room-filter"
          />
        </div>

        {/* E-Invoice Status */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Trạng thái HĐĐT
            <ChevronUp className="w-4 h-4" />
          </h4>
          <div className="space-y-1.5">
            {EINVOICE_STATUS_OPTIONS.map(opt => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="einvoice"
                  checked={einvoiceFilter === opt.key}
                  onChange={() => { setEinvoiceFilter(opt.key); setPage(1); }}
                  className="accent-blue-600"
                />
                <span className="text-sm text-gray-600 dark:text-slate-400">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Sales Channel */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Kênh bán
            <ChevronUp className="w-4 h-4" />
          </h4>
          <Input
            placeholder="Chọn kênh bán"
            className="text-sm"
            value={channelFilter}
            onChange={e => { setChannelFilter(e.target.value); setPage(1); }}
            data-testid="channel-filter"
          />
        </div>

        {/* Page Size */}
        <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
          <label className="text-sm text-gray-600 dark:text-slate-400 flex items-center gap-2">
            Số bản ghi:
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-700 dark:text-slate-200 text-sm"
              data-testid="page-size-select"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 gap-2 border-b border-gray-200 dark:border-slate-700">
          <h1 className="text-xl font-bold text-gray-800 dark:text-slate-100" data-testid="invoices-title">
            Hóa đơn
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => navigate("/pos")}
              className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1"
              data-testid="btn-new-order"
            >
              <Plus className="w-4 h-4" /> Nhận gọi món
            </Button>
            <Button
              onClick={handleSync}
              variant="outline"
              className="text-sm gap-1"
              disabled={syncing}
              data-testid="btn-sync-kiotviet"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Đang đồng bộ..." : "Đồng bộ KiotViet"}
            </Button>
            <Button
              onClick={handleExport}
              className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1"
              data-testid="btn-export"
            >
              <Download className="w-4 h-4" /> Xuất file
            </Button>
            <div className="flex border border-gray-300 dark:border-slate-600 rounded overflow-hidden">
              <button
                onClick={() => setViewMode("table")}
                className={`p-1.5 ${viewMode === "table" ? "bg-blue-600 text-white" : "bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300"}`}
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 ${viewMode === "grid" ? "bg-blue-600 text-white" : "bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300"}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-slate-700">
                <th className="w-10 px-3 py-2.5">
                  <Checkbox
                    checked={selectedIds.length === invoices.length && invoices.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Mã hóa đơn</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Thời gian (Giờ đi)</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Khách hàng</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300">Tổng tiền hàng</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300">Giảm giá</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300">Tiền thuế</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300">Khách đã trả</th>
              </tr>
              {/* Summary Row */}
              <tr className="border-b border-gray-200 dark:border-slate-600 bg-blue-50/50 dark:bg-blue-900/20">
                <td colSpan={4} className="px-3 py-2"></td>
                <td className="px-3 py-2 text-right font-bold text-blue-700 dark:text-blue-300">{formatVND(summary.total_amount || 0)}</td>
                <td className="px-3 py-2 text-right font-bold text-blue-700 dark:text-blue-300">{formatVND(summary.total_discount || 0)}</td>
                <td className="px-3 py-2 text-right font-bold text-blue-700 dark:text-blue-300">{formatVND(summary.total_tax || 0)}</td>
                <td className="px-3 py-2 text-right font-bold text-blue-700 dark:text-blue-300">{formatVND(summary.total_paid || 0)}</td>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="text-center py-10 text-gray-400">Đang tải...</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-gray-400">Không có hóa đơn nào</td></tr>
              ) : (
                invoices.map(inv => (
                  <React.Fragment key={inv.id}>
                    {/* Invoice Row */}
                    <tr
                      className={`border-b border-gray-100 dark:border-slate-700 cursor-pointer transition-colors ${
                        expandedRow === inv.id
                          ? "bg-blue-50 dark:bg-blue-900/30 font-medium"
                          : "hover:bg-gray-50 dark:hover:bg-slate-800/50"
                      }`}
                      onClick={() => toggleRow(inv.id)}
                      data-testid={`invoice-row-${inv.code}`}
                    >
                      <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(inv.id)}
                          onCheckedChange={() => setSelectedIds(prev =>
                            prev.includes(inv.id) ? prev.filter(i => i !== inv.id) : [...prev, inv.id]
                          )}
                        />
                      </td>
                      <td className="px-3 py-2.5 text-gray-800 dark:text-slate-200">{inv.code}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{formatDateTime(inv.purchase_date)}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{inv.customer_name}</td>
                      <td className="px-3 py-2.5 text-right text-gray-800 dark:text-slate-200">{formatVND(inv.total)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600 dark:text-slate-400">{formatVND(inv.discount)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600 dark:text-slate-400">{formatVND(inv.tax)}</td>
                      <td className="px-3 py-2.5 text-right font-medium text-gray-800 dark:text-slate-200">{formatVND(inv.customer_paid)}</td>
                    </tr>

                    {/* Expanded Detail */}
                    {expandedRow === inv.id && (
                      <tr className="bg-blue-50/30 dark:bg-blue-900/10">
                        <td colSpan={8} className="p-0">
                          <InvoiceDetail
                            invoice={inv}
                            detailTab={detailTab}
                            setDetailTab={setDetailTab}
                            onPrint={() => handlePrint(inv)}
                            onCopy={() => handleCopy(inv.id)}
                            onCancel={() => setCancelDialog(inv)}
                            onExport={handleExport}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 py-2 sm:py-3 gap-2 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={page <= 1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30">
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (page <= 3) pageNum = i + 1;
              else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = page - 2 + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 rounded text-sm ${page === pageNum ? "bg-blue-600 text-white" : "hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-400"}`}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && page < totalPages - 2 && <span className="px-1 text-gray-400">...</span>}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30">
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
          <span className="text-sm text-gray-500 dark:text-slate-400" data-testid="pagination-info">
            Hiển thị {totalCount > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, totalCount)} trên tổng số {totalCount} hóa đơn
          </span>
        </div>
      </main>

      <MobileFilterToggle showFilter={showFilter} setShowFilter={setShowFilter} />

      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Xác nhận hủy hóa đơn</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Bạn có chắc muốn hủy hóa đơn <strong>{cancelDialog?.code}</strong>? Thao tác này không thể hoàn tác.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelDialog(null)}>Đóng</Button>
            <Button variant="destructive" onClick={() => handleCancel(cancelDialog?.id)} data-testid="confirm-cancel-invoice">
              Hủy hóa đơn
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Invoice Detail Component ---
function InvoiceDetail({ invoice, detailTab, setDetailTab, onPrint, onCopy, onCancel, onExport }) {
  return (
    <div className="px-6 py-4 border-t border-blue-100 dark:border-blue-800" data-testid="invoice-detail-panel">
      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mb-4">
        <Button size="sm" variant="outline" className="text-xs gap-1" data-testid="btn-update-invoice">
          <Edit className="w-3 h-3" /> Cập nhật
        </Button>
        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs gap-1" data-testid="btn-save-invoice">
          <Save className="w-3 h-3" /> Lưu
        </Button>
        <Button size="sm" variant="outline" className="text-xs gap-1" onClick={onPrint} data-testid="btn-print-invoice">
          <Printer className="w-3 h-3" /> In
        </Button>
        <Button size="sm" variant="outline" className="text-xs gap-1" onClick={onExport} data-testid="btn-export-invoice">
          <Download className="w-3 h-3" /> Xuất file
        </Button>
        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs gap-1" onClick={onCopy} data-testid="btn-copy-invoice">
          <Copy className="w-3 h-3" /> Sao chép
        </Button>
        <Button size="sm" variant="destructive" className="text-xs gap-1" onClick={onCancel} data-testid="btn-cancel-invoice">
          <X className="w-3 h-3" /> Hủy bỏ
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200 dark:border-slate-700 mb-4">
        <button
          onClick={() => setDetailTab("info")}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            detailTab === "info" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700"
          }`}
          data-testid="tab-info"
        >
          Thông tin
        </button>
        <button
          onClick={() => setDetailTab("payments")}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            detailTab === "payments" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700"
          }`}
          data-testid="tab-payments"
        >
          Lịch sử thanh toán
        </button>
      </div>

      {detailTab === "info" ? (
        <InvoiceInfoTab invoice={invoice} />
      ) : (
        <InvoicePaymentsTab invoice={invoice} />
      )}
    </div>
  );
}

// --- Info Tab ---
function InvoiceInfoTab({ invoice }) {
  return (
    <div>
      {/* Invoice Info Grid */}
      <div className="grid grid-cols-2 gap-x-12 gap-y-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Mã hóa đơn:</span>
          <span className="text-sm font-medium text-gray-800 dark:text-slate-200">{invoice.code}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Trạng thái:</span>
          <span className={`text-sm font-medium ${
            invoice.status === "completed" ? "text-green-600" :
            invoice.status === "cancelled" ? "text-red-600" : "text-yellow-600"
          }`}>{invoice.status_label}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Giờ đến:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{formatDateTime(invoice.arrival_time)}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Phòng/bàn:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{invoice.room_table || "-"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Thời gian:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{formatDateTime(invoice.purchase_date)}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Chi nhánh:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{invoice.branch_name || "Karinox Arena"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Khách hàng:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{invoice.customer_name}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Người nhận đơn:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{invoice.receiver_name || "-"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Bảng giá:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{invoice.price_list || "Bảng giá chung"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Người tạo:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{invoice.creator_name || "-"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Mã đặt hàng:</span>
          <span className="text-sm text-blue-600 dark:text-blue-400">{invoice.order_code || "-"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Số khách:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{invoice.num_guests || 1}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Kênh bán:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{invoice.sales_channel || "Khách đến trực tiếp"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Ghi chú:</span>
          <span className="text-sm text-gray-500 dark:text-slate-400 italic">{invoice.note || "Không có"}</span>
        </div>
      </div>

      {/* Products Table */}
      <div className="border border-gray-200 dark:border-slate-700 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-slate-700">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Mã hàng hóa</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Tên hàng</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Số lượng</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Đơn giá</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Giảm giá</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Giá bán</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Thành tiền</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.details || []).map((d, idx) => (
              <tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
                <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{d.product_code}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{d.product_name}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-slate-300">{d.quantity}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-slate-300">{formatVND(d.price)}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-slate-300">{formatVND(d.discount || 0)}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-slate-300">{formatVND(d.sell_price)}</td>
                <td className="px-3 py-2 text-right font-medium text-gray-800 dark:text-slate-200">{formatVND(d.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="mt-3 flex justify-end">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-8">
            <span className="text-gray-500 dark:text-slate-400">Tổng số lượng:</span>
            <span className="font-medium text-gray-800 dark:text-slate-200">
              {(invoice.details || []).reduce((sum, d) => sum + (d.quantity || 0), 0)}
            </span>
          </div>
          <div className="flex justify-between gap-8">
            <span className="text-gray-500 dark:text-slate-400">Tổng tiền hàng:</span>
            <span className="font-medium text-gray-800 dark:text-slate-200">{formatVND(invoice.total)}</span>
          </div>
          <div className="flex justify-between gap-8">
            <span className="text-gray-500 dark:text-slate-400">Giảm giá:</span>
            <span className="text-gray-700 dark:text-slate-300">{formatVND(invoice.discount)}</span>
          </div>
          <div className="flex justify-between gap-8 pt-1 border-t border-gray-200 dark:border-slate-700">
            <span className="font-semibold text-gray-800 dark:text-slate-100">Khách đã trả:</span>
            <span className="font-bold text-blue-700 dark:text-blue-300">{formatVND(invoice.customer_paid)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Payments Tab ---
function InvoicePaymentsTab({ invoice }) {
  const payments = invoice.payments || [];

  if (payments.length === 0) {
    return <p className="text-sm text-gray-400 dark:text-slate-500 py-4">Chưa có lịch sử thanh toán</p>;
  }

  return (
    <div className="border border-gray-200 dark:border-slate-700 rounded overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-100 dark:bg-slate-700">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Mã phiếu</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Thời gian</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Người tạo</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Người nhận đơn</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Phương thức</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Trạng thái</th>
            <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Tiền thu</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p, idx) => (
            <tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
              <td className="px-3 py-2 text-blue-600 dark:text-blue-400">{p.code || "-"}</td>
              <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{formatDateTime(p.time)}</td>
              <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{p.creator || "-"}</td>
              <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{p.receiver || "-"}</td>
              <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{p.method || "-"}</td>
              <td className="px-3 py-2 text-green-600">{p.status || "Da thanh toan"}</td>
              <td className="px-3 py-2 text-right font-medium text-gray-800 dark:text-slate-200">{formatVND(p.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
